import { FieldArray } from "formik";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import type { ClientContactFormRow } from "features/Clients/utils/clientFormPayload";

const emptyRow = (): ClientContactFormRow => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  designation: "",
  isPrimary: false,
});

type Props = {
  validation: any;
};

export default function ClientContactsFieldArray({ validation }: Props) {
  const items: ClientContactFormRow[] = validation.values.contacts ?? [];

  const setPrimary = (index: number) => {
    const next = (validation.values.contacts as ClientContactFormRow[]).map(
      (row, i) => ({
        ...row,
        isPrimary: i === index,
      }),
    );
    validation.setFieldValue("contacts", next);
  };

  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">Contacts</h5>
        <p className="text-muted small mb-0 mt-1">
          People at this client. Mark exactly one as primary when you add
          contacts.
        </p>
      </CardHeader>
      <CardBody>
        <FieldArray name="contacts">
          {({ push, remove }) => (
            <>
              {items.map((row, index) => (
                <Card key={index} className="border mb-3">
                  <CardBody className="py-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <span className="text-muted fw-medium small">
                        Contact {index + 1}
                      </span>
                      <div className="hstack gap-2">
                        <div className="form-check">
                          <Input
                            id={`contact-primary-${index}`}
                            type="checkbox"
                            className="form-check-input"
                            checked={Boolean(row.isPrimary)}
                            onChange={() => setPrimary(index)}
                          />
                          <Label
                            className="form-check-label small"
                            for={`contact-primary-${index}`}
                          >
                            Primary
                          </Label>
                        </div>
                        <Button
                          type="button"
                          color="soft-danger"
                          size="sm"
                          outline
                          onClick={() => remove(index)}
                          disabled={items.length <= 1}
                        >
                          <i className="ri-delete-bin-line me-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Label className="form-label">First name</Label>
                        <Input
                          name={`contacts[${index}].firstName`}
                          value={row.firstName}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.contacts?.[index]
                                ?.firstName &&
                              validation.errors.contacts?.[index]?.firstName
                            )
                          }
                        />
                        {validation.touched.contacts?.[index]?.firstName &&
                        validation.errors.contacts?.[index]?.firstName ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(
                              validation.errors.contacts?.[index]?.firstName,
                            )}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">Last name</Label>
                        <Input
                          name={`contacts[${index}].lastName`}
                          value={row.lastName}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.contacts?.[index]
                                ?.lastName &&
                              validation.errors.contacts?.[index]?.lastName
                            )
                          }
                        />
                        {validation.touched.contacts?.[index]?.lastName &&
                        validation.errors.contacts?.[index]?.lastName ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(
                              validation.errors.contacts?.[index]?.lastName,
                            )}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">Email</Label>
                        <Input
                          type="email"
                          name={`contacts[${index}].email`}
                          value={row.email}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="form-label">Phone</Label>
                        <Input
                          name={`contacts[${index}].phone`}
                          value={row.phone}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                      <Col md={12}>
                        <Label className="form-label">Designation</Label>
                        <Input
                          name={`contacts[${index}].designation`}
                          value={row.designation}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
              <Button
                type="button"
                color="soft-secondary"
                className="fw-medium"
                onClick={() => push(emptyRow())}
              >
                <i className="ri-add-fill me-1" />
                Add contact
              </Button>
              {validation.touched.contacts &&
              typeof validation.errors.contacts === "string" ? (
                <div className="text-danger small mt-2">
                  {validation.errors.contacts}
                </div>
              ) : null}
            </>
          )}
        </FieldArray>
      </CardBody>
    </Card>
  );
}
